import React, { useEffect, useState, useRef } from 'react';
import useWindowSize from '../../../hooks/useWindowSize';
import {calculateMinColumnWidths} from '../../../helpers/calculateMinColumnWidths';

import { connect } from 'react-redux';

import { process as kendoProcess, orderBy, filterBy } from '@progress/kendo-data-query';
import { Grid, getSelectedState, GridColumn as Column } from '@progress/kendo-react-grid';
import {
    ExcelExport,
    ExcelExportColumn
} from '@progress/kendo-react-excel-export';
import { getter } from '@progress/kendo-react-common';
import ContactsTableContextMenu from './ContactsTableContextMenu';
import BobLoadingMask from '../../../components/BobLoadingMask';
import HtmlTooltip from '../../../components/HtmlTooltip/HtmlTooltip';

import * as ContactActions from '../../../actions/contactActions';

import {parsePhoneNumber} from 'libphonenumber-js';
import { kendoGridInlineStyling } from '../../../helpers/kendoGridInlineStyleHelper';
import * as classes from '../ClientPage/Table.module.css';
import { groupBy, isEmpty, cloneDeep } from 'lodash';

const idGetter = getter('id');

const initialDataState = {
    filter: [],
    sort:   [],
    take:   10,
    skip:   0
};

const ContactsTable = ({
    _contactExport,
    activeNavClient,
    activeNavSite,
    bobLoading,
    clientContacts,
    clientContactsMeta,
    contactTableSelectState,
    currentUser,
    deleteContact,
    fetchClientContacts,
    fetchSiteContacts,
    handleContactExport,
    refreshTab,
    setRefreshTab,
    setClientContacts,
    setContactTableSelectState,
    setSiteContacts,
    siteContacts,
    siteContactsMeta,
    view,
    workbookModeEnabled,
    ...props
}) => {

    const [dataState, setDataState] = useState(initialDataState);
    const [pageable] = useState({ pageSizes: true });
    const [contacts, setContacts] = useState([]);
    const [emailColumnWidth, setEmailColumnWidth] = useState(450);
    const [take, setTake] = useState(10);

    const gridDivRef = useRef(null);
    const gridRef = useRef(null);

    const windowSize = useWindowSize();

    useEffect(() => {
        if (!workbookModeEnabled) {
            initialDataState.take = take;
            setDataState(initialDataState);
        }
    }, [workbookModeEnabled]);

    useEffect(() => {
        const columns = gridRef.current.props.children;
        setEmailColumnWidth(calculateMinColumnWidths(columns, gridDivRef.current.offsetWidth, 'email', 450, true));
    }, [windowSize]);

    useEffect(() => {
        const init = async () => {
            await fetchClientContacts(activeNavClient.id, { includeClientSites: true });
        };

        if (activeNavClient.id && !activeNavSite.id && (view === 'client' || view === 'Client Contact')) init();
    }, [activeNavClient, view]);

    useEffect(() => {
        const init = async () => {
            await fetchSiteContacts(activeNavClient.id, activeNavSite.id);
        };

        if (activeNavSite.id && (view === 'site' || view === 'Site Contact')) init();
    }, [activeNavSite, view]);

    useEffect(() => {
        if (clientContacts) {
            let contactList = clientContacts.map((dataItem) =>
                Object.assign(
                    {
                        selected: false,
                    },
                    dataItem
                ));

            const filteredList = contactList.filter(item => !item.siteId);
            const grouped = groupBy(contactList, contact => contact.userId);

            for (let key in grouped) {
                let array = grouped[key];
                array = array.filter(item => item.siteId);

                if (array) {
                    const index = filteredList.findIndex(item => item.userId === key);
                    let sites = [];
                    array.forEach(item => {
                        sites.push(item.siteName);
                    });

                    if (index >= 0) {
                        filteredList[index].sites = sites;
                    }
                }
            }

            setContacts(filteredList);
        }
    }, [clientContacts]);

    useEffect(() => {
        if (siteContacts) {
            const contactList = siteContacts.map((dataItem) =>
                Object.assign(
                    {
                        selected: false,
                    },
                    dataItem
                ));
            setContacts(contactList);
        }
    }, [siteContacts]);
    
    useEffect(() => {
        return () => {
            setClientContacts([], {});
            setSiteContacts([], {});
        };
    }, []);

    useEffect(() => {
        const columns = gridRef.current.props.children;
        setEmailColumnWidth(calculateMinColumnWidths(columns, gridDivRef.current.offsetWidth, 'email', 450, true));
    }, []);

    useEffect(() => {
        const refresh = async () => {
            if (view === 'client' || view === 'Client Contact') {
                await fetchClientContacts(activeNavClient.id, {includeClientSites: true});
            } else if (view === 'site' || view === 'Site Contact') {
                await fetchSiteContacts(activeNavClient.id, activeNavSite.id);
            }

            setRefreshTab('');
        };

        if (refreshTab) refresh();
    }, [refreshTab]);

    const actionsCell = props => {
        return (
            <td className={props.className} style={props.style}>
                <ContactsTableContextMenu
                    deleteContactData={handleDeleteContact}
                    dataItem={props.dataItem}
                />
            </td>
        );
    };

    const handleDeleteContact = async dataItem => {
        await deleteContact(dataItem.id);

        if (view === 'client') {
            await fetchClientContacts(activeNavClient.id, { includeClientSites: true });
        } else if (view === 'site') {
            await fetchSiteContacts(activeNavClient.id, activeNavSite.id);
        }
    };

    const onHeaderSelectionChange = event => {
        const checkboxElement = event.syntheticEvent.target;
        const checked = checkboxElement.checked;
        const newSelectedState = cloneDeep(contactTableSelectState);

        const filteredData = orderBy(filterBy(contacts, dataState.filter), dataState.sort);
        filteredData.forEach((item) => {
            newSelectedState[idGetter(item)] = checked;
        });
        setContactTableSelectState(newSelectedState);
    };

    const onSelectionChange = React.useCallback(
        (event) => {
            const newSelectedState = getSelectedState({
                event,
                selectedState: contactTableSelectState,
                dataItemKey:   'id',
            });
            setContactTableSelectState(newSelectedState);
        },
        [contactTableSelectState]
    );

    const renderNameCell = props => {
        const isBold = !activeNavSite && !activeNavSite.id && props.dataItem.id === activeNavClient.primaryContact.id;

        return (
            <td>
                <span className={isBold ? classes.Bold : null}>
                    {props.dataItem.userName.fullName}
                </span>
            </td>
        );
    };

    const renderTitleCell = props => {
        return (
            <td>
                {props.dataItem.titleDescription}
            </td>
        );
    };

    const renderPrimaryNumberCell = props => {
        const primaryNumber = props.dataItem.userAssignmentPhones.find(item => item.isPrimary === true);

        let formattedNumber = '';

        if (primaryNumber) {
            formattedNumber = parsePhoneNumber(primaryNumber.phoneNumber.number);
            if (formattedNumber) formattedNumber = formattedNumber.formatInternational();
        }

        return (
            <td>
                {formattedNumber}
            </td>
        );
    };

    const renderSMSNumberCell = props => {
        const SMSNumber = props.dataItem.userAssignmentPhones.find(item => item.isSMS === true);

        let formattedNumber = '';

        if (SMSNumber) {
            formattedNumber = parsePhoneNumber(SMSNumber.phoneNumber.number);
            if (formattedNumber) formattedNumber = formattedNumber.formatInternational();
        }

        return (
            <td>
                {formattedNumber}
            </td>
        );
    };

    const renderSitesCell = props => {
        const sites = props.dataItem.sites;

        const buildTooltip = () => {
            return (
                <div>
                    {sites.map(item => <p>{item}</p>)}
                </div>
            );
        };

        if (sites.length > 1) {
            return (
                <td>
                    <HtmlTooltip
                        information={buildTooltip()}
                        placement={'top-start'}
                        body={
                            <div>{sites.length}</div>
                        }
                    />
                </td>
            );
        } else if (sites.length === 0) {
            return (
                <td>
                    None
                </td>
            );
        } else {
            return (
                <td>
                    {sites[0]}
                </td>
            );
        }
    };

    const onDataStateChange = e => {
        setTake(e.dataState.take);
        setDataState(e.dataState);
    };

    return (
        <React.Fragment>
            <style>
                { kendoGridInlineStyling }
            </style>
            <div ref={gridDivRef} className={classes.Table}>
                <ExcelExport data={contacts} ref={_contactExport}>
                    <ExcelExportColumn
                        field="userName.fullName"
                        title={'Name'}
                        cell={renderNameCell}
                        id={'nameColumn'}
                    />
                    <ExcelExportColumn field="email" title={'Email'} width="auto" />
                    <ExcelExportColumn
                        field="titleDescription"
                        title={'Title'}
                        cell={renderTitleCell}
                        id={'titleColumn'}
                    />
                    <ExcelExportColumn
                        field="primary"
                        title={'Primary Number'}
                        cell={renderPrimaryNumberCell}
                        id={'primaryNumberColumn'}
                    />
                    <ExcelExportColumn
                        field="sms"
                        title={'SMS Number'}
                        cell={renderSMSNumberCell}
                        id={'smsNumberColumn'}
                    />

                </ExcelExport>
                <Grid
                    ref={gridRef}
                    sortable={{
                        mode: 'multiple'
                    }}
                    className={classes.KendoGrid}
                    data={kendoProcess(contacts.map((item) => ({
                        ...item, 'selected': contactTableSelectState[idGetter(item)],
                    })), dataState)}
                    {...dataState}
                    dataItemKey="id"
                    selectedField="selected"
                    selectable={{
                        enabled: true,
                        drag:    false,
                        cell:    false,
                        mode:    'multiple'
                    }}
                    onDataStateChange={onDataStateChange}
                    filterable={workbookModeEnabled}
                    onSelectionChange={onSelectionChange}
                    onHeaderSelectionChange={onHeaderSelectionChange}
                    pageable={pageable}
                    reorderable={false}
                >
                    <Column
                        filterable={false}
                        field="selected"
                        width={60}
                        headerSelectionValue={
                            orderBy(filterBy(contacts, dataState.filter), dataState.sort).length > 0 && 
                            orderBy(filterBy(contacts, dataState.filter), dataState.sort).findIndex((item) => !contactTableSelectState[idGetter(item)]) === -1
                        }
                    />
                    <Column
                        field="userName.fullName"
                        title={'Name'}
                        cell={renderNameCell}
                        id={'nameColumn'}
                        width={300}
                    />
                    {
                        (view === 'client' || view === 'Client Contact') ?
                            <Column
                                field="sites"
                                title="Site"
                                cell={renderSitesCell}
                                width={250}
                            />
                            :
                            null
                    }
                    <Column
                        field="email"
                        title={'Email'}
                        width={emailColumnWidth}
                    />
                    <Column
                        field="titleDescription"
                        title={'Title'}
                        cell={renderTitleCell}
                        id={'titleColumn'}
                        width={280}
                    />
                    <Column
                        field="primaryNumber"
                        title={'Primary Number'}
                        cell={renderPrimaryNumberCell}
                        id={'primaryNumberColumn'}
                        width={220}
                    />
                    <Column
                        field="smsNumber"
                        title={'SMS Number'}
                        cell={renderSMSNumberCell}
                        id={'smsNumberColumn'}
                        width={220}
                    />
                    <Column
                        field=""
                        title=""
                        width={80}
                        locked={true}
                        sortable={false}
                        filterable={false}
                        cell={actionsCell}
                    />
                </Grid>
                {
                    ((view === 'client' || view === 'Client Contact') && isEmpty(clientContactsMeta)) || ((view === 'site' || view === 'Site Contact') && isEmpty(siteContactsMeta))
                    || bobLoading.includes('DeleteContact') || bobLoading.includes('FetchClientContacts') || bobLoading.includes('FetchSiteContacts') ?
                        <BobLoadingMask />
                        :
                        null
                }
            </div>
        </React.Fragment>
    );
};

const mapStateToProps = state => {
    return {
        activeNavClient:    state.bob.activeNavClient,
        activeNavSite:      state.bob.activeNavSite,
        bobLoading:         state.bob.bobLoading,
        clientContacts:     state.contact.clientContacts,
        clientContactsMeta: state.contact.clientContactsMeta,
        currentUser:        state.currentUser,
        siteContacts:       state.contact.siteContacts,
        siteContactsMeta:   state.contact.siteContactsMeta
    };
};

const mapDispatchToProps = {
    ...ContactActions
};

export default connect(mapStateToProps, mapDispatchToProps)(ContactsTable);
